import './App.css';
import {Box, CircularProgress, ThemeProvider} from '@mui/material';
import theme from './theme';
import AppIntlProvider from './hocs/AppIntlProvider';
import routes from './routes';
import {useLocation, useNavigate, useRoutes} from 'react-router-dom';
import {ConnectionChanel} from './services';
import {FC, useEffect} from 'react';
import 'react-toastify/dist/ReactToastify.css';
import {ToastContainer} from 'react-toastify';
import {useAppDispatch, useAppSelector} from './app/hooks';
import {useStyles} from './App.sx';
import CryptoJs from 'crypto-js';
import {cryptoKey} from 'src/axiosBaseQuery/interceprtors';
import ConnectionProvider from './context/ConnectionContext';
import {webSocketMessage} from './stores/common/commonSlice';
import {AppRouteEnum, AppRouteSpace} from './utils/helper';
import {useGetMyPermissionsQuery} from './api';
import {setAccess} from './stores/common/commonSlice';
import {resolveLandingRoute} from './utils/resolveLandingRoute';

interface AppProps {
    connect: ConnectionChanel;
}

const App: FC<AppProps> = ({connect}) => {
    const routing = useRoutes(routes());
    const location = useLocation();

    const dispatch = useAppDispatch();
    const isLoaded = useAppSelector((state) => state.common.isLoaded);
    const authInfo = useAppSelector((state) => state.common.authInfo);
    const access = useAppSelector((state) => state.common.access);
    const styles = useStyles;

    // Everything the UI shows now hangs off this one call: every permission gate, the route guards
    // and the landing route all read it. Skipped while logged out, since /me/permissions needs a token.
    const {data: myPermissions} = useGetMyPermissionsQuery(authInfo?.id as number, {
        skip: !authInfo?.id,
    });

    useEffect(() => {
        if (myPermissions) {
            dispatch(setAccess(myPermissions));
        }
    }, [myPermissions]);
    const navigate = useNavigate();
    const conditionPath =
        location.pathname === `/${AppRouteEnum.login}` ||
        location.pathname === `/${AppRouteEnum.forgotPassword}` ||
        location.pathname === `/${AppRouteEnum.resetPassword}` ||
        location.pathname === `/${AppRouteEnum.signUp}` ||
        location.pathname === `/${AppRouteEnum.verifyEmail}` ||
        location.pathname === `/${AppRouteEnum.confirmRegistration}` ||
        location.pathname === `/${AppRouteEnum.corporateUserWelcome}`;
    const services: Array<string> = [
        AppRouteEnum.privacyPolicyEn,
        AppRouteEnum.privacyPolicyFr,
        AppRouteEnum.privacyPolicyDe,
        AppRouteEnum.termsOfUse,
        AppRouteEnum.termsOfUseEn,
        AppRouteEnum.termsOfUseFr,
        AppRouteEnum.termsOfUseDe,
        AppRouteEnum.support,
        AppRouteEnum.privacyPolicy,
        AppRouteEnum.support,
        AppRouteEnum.supportEn,
        AppRouteEnum.supportFr,
        AppRouteEnum.supportDe,
    ];
    const pageIsService = services.includes(location.pathname.slice(1));

    useEffect(() => {
        const localStorageRemember = localStorage.getItem('remember') ?? '';
        const localRememberValue = localStorageRemember
            ? JSON.parse(
                CryptoJs.AES.decrypt(localStorageRemember, cryptoKey.toString()).toString(
                    CryptoJs.enc.Utf8
                )
            )
            : '';
        const isDataUnavailable = !Object.keys(authInfo).length || !localRememberValue;
        if (
            !pageIsService &&
            isDataUnavailable &&
            !sessionStorage.getItem('remember') &&
            conditionPath
        ) {
            navigate('/');
        }
    }, [authInfo]);

    useEffect(() => {
        if (Object.keys(authInfo).length) {
            // The socket opened before anyone had signed in, so it is carrying no token and the
            // server will refuse this topic to it. Reconnects only if the stored token has changed.
            connect.ensureAuthenticated();
            connect.onSubscribe(authInfo.uuid, (mes: string) => {
                dispatch(webSocketMessage(mes));
            });
        }
        if (location.pathname === '/') {
            if (!Object.keys(authInfo).length) {
                navigate(AppRouteSpace([AppRouteEnum.login]));
                return;
            }
            // Same reason as the index route: without permissions the answer is the fallback, and the
            // fallback is the registration screen. Waiting costs a frame; guessing costs a flash of
            // somebody else's page.
            if (!access) {
                return;
            }
            // One rule, shared with the route guards: the first page this user can actually open.
            // This used to branch on the role name here, again in routes.tsx and a third time in
            // LoginForm - three answers that had already drifted apart from each other.
            navigate(resolveLandingRoute(access));
        }
    }, [location]);
    return (
        <ThemeProvider theme={theme}>
            <AppIntlProvider>
                <ConnectionProvider connect={connect}>
                    {routing}
                    {isLoaded && (
                        <Box sx={styles.preloaderWrapper}>
                            <CircularProgress size={80}/>
                        </Box>
                    )}
                    <ToastContainer/>
                </ConnectionProvider>
            </AppIntlProvider>
        </ThemeProvider>
    );
};

export default App;
